import type { ShadcnAppConfig, ShadcnLayoutType, ShadcnOpenApiSpec, ShadcnPageConfig } from './types';

interface GenerateShadcnAppConfigOptions {
  appId: string;
  apiBaseUrl: string;
}

function hasAggregateStatsEndpoint(spec: ShadcnOpenApiSpec): boolean {
  return Object.keys(spec.paths || {}).some(path => /aggregate|stats?/i.test(path));
}

function schemaHasStatusOrStageEnum(schema: unknown): boolean {
  if (!schema || typeof schema !== 'object') {
    return false;
  }

  const properties = (schema as { properties?: Record<string, unknown> }).properties;
  if (!properties || typeof properties !== 'object') {
    return false;
  }

  return ['status', 'stage'].some((field) => {
    const fieldSchema = properties[field];
    if (!fieldSchema || typeof fieldSchema !== 'object') {
      return false;
    }

    const enumValues = (fieldSchema as { enum?: unknown[] }).enum;
    return Array.isArray(enumValues) && enumValues.length > 0;
  });
}

function inferDefaultLayoutType(spec: ShadcnOpenApiSpec): ShadcnLayoutType {
  const schemas = Object.values(spec.components?.schemas ?? {});
  if (schemas.some(schemaHasStatusOrStageEnum)) {
    return 'kanban';
  }

  if (hasAggregateStatsEndpoint(spec)) {
    return 'analytics-grid';
  }

  return 'split-panel';
}

function buildDashboardPage(): ShadcnPageConfig {
  return {
    id: 'dashboard',
    title: 'Dashboard',
    path: '/dashboard',
    type: 'dashboard',
    layout: {
      type: 'full',
    },
    widgets: [
      {
        type: 'kpi',
        label: 'Total Revenue',
        entity: 'orders',
        aggregation: 'revenue',
        span: 4,
        valueKey: 'totalRevenue',
        trendKey: 'trend',
      },
      {
        type: 'bar-chart',
        label: 'Revenue by month',
        entity: 'orders',
        aggregation: 'revenue-by-month',
        span: 8,
        xKey: 'month',
        yKey: 'revenue',
      },
      {
        type: 'recent-list',
        label: 'Recent activity',
        entity: 'activity',
        aggregation: 'recent',
        span: 12,
        columns: ['event', 'actor', 'timestamp'],
      },
    ],
  };
}

function toSegment(path: string): string | null {
  const [withoutQuery] = path.split('?');
  const segments = withoutQuery.split('/').filter(Boolean);
  const first = segments[0];
  if (!first) {
    return null;
  }

  return first;
}

function buildTabsPageFromGroup(group: string, paths: string[]): ShadcnPageConfig {
  const deduped = Array.from(new Set(paths));

  return {
    id: `${group}-workspace`,
    title: `${group[0]?.toUpperCase() ?? ''}${group.slice(1)} Workspace`,
    path: `/${group}`,
    type: 'tabs',
    tabs: {
      defaultTabId: `${group}-overview`,
      items: [
        {
          id: `${group}-overview`,
          label: 'Overview',
          pageConfig: {
            type: 'dashboard',
            title: `${group} analytics`,
            widgets: [
              {
                type: 'recent-list',
                endpoint: deduped[0] ?? `/${group}`,
                columns: ['id', 'name', 'updatedAt'],
              },
            ],
          },
        },
        {
          id: `${group}-records`,
          label: 'Records',
          loadingEndpoint: deduped[0] ?? `/${group}`,
          pageConfig: {
            type: 'crud-list',
            title: `${group} records`,
            table: {
              title: `${group} table`,
              columns: [
                { key: 'id', header: 'ID', sortable: true, filterable: true },
                { key: 'name', header: 'Name', sortable: true, filterable: true },
              ],
              data: [],
              searchable: true,
            },
            form: {
              submitLabel: `Create ${group}`,
              fields: [
                { name: 'name', label: 'Name', type: 'text', required: true },
              ],
            },
          },
        },
        {
          id: `${group}-workflow`,
          label: 'Workflow',
          pageConfig: {
            type: 'table',
            title: `${group} workflow`,
            table: {
              columns: [
                { key: 'status', header: 'Status' },
                { key: 'owner', header: 'Owner' },
              ],
              data: [],
            },
          },
        },
      ],
    },
  };
}

function buildTabsPagesFromSpec(spec: ShadcnOpenApiSpec): ShadcnPageConfig[] {
  const grouped = new Map<string, string[]>();

  Object.keys(spec.paths ?? {}).forEach((path) => {
    const group = toSegment(path);
    if (!group) {
      return;
    }

    const entries = grouped.get(group) ?? [];
    entries.push(path);
    grouped.set(group, entries);
  });

  return Array.from(grouped.entries())
    .filter(([, paths]) => paths.length >= 3 && paths.some((path) => /\{[^}]+\}/.test(path)))
    .map(([group, paths]) => buildTabsPageFromGroup(group, paths));
}

export function generateShadcnAppConfig(
  spec: ShadcnOpenApiSpec,
  options: GenerateShadcnAppConfigOptions,
): ShadcnAppConfig {
  const defaultLayoutType = inferDefaultLayoutType(spec);
  const pages: ShadcnPageConfig[] = [
    {
      id: 'home',
      title: 'Home',
      path: '/',
      type: 'detail',
      layout: {
        type: defaultLayoutType,
      },
    },
  ];

  if (hasAggregateStatsEndpoint(spec)) {
    pages.unshift(buildDashboardPage());
  }

  pages.push(...buildTabsPagesFromSpec(spec));

  return {
    id: options.appId,
    name: spec.info?.title || options.appId,
    apiBaseUrl: options.apiBaseUrl,
    plan: 'free',
    pages,
  };
}
